import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityId,
} from '@reduxjs/toolkit';
import { postJSON } from 'api/fetch';
import { RootState } from 'initializers/types';
import createCachedSelector from 're-reselect';

import { MAX_HOURS_IN_DAY_VIEW } from 'features/scheduleBuilder/ScheduleBuilderView/DayView/constants';
import {
  selectCurrentDateRangeEnd,
  selectCurrentDateRangeStart,
  selectJobIdUserIdMap,
  selectJobIdWageRateMap,
  selectOvernightShiftsForDay,
  selectShiftIdJobIdMap,
} from 'features/scheduleBuilder/ScheduleBuilderView/selectors/shared';
import { getDecimalTimeRange } from 'features/scheduleBuilder/ScheduleBuilderView/utils/dayViewData';
import { updateOvernightTotals } from 'features/scheduleBuilder/ScheduleBuilderView/utils/roleLaborTotals';

import { datesInRange } from 'util/dateTime';
import { toCurrency } from 'util/toCurrency';

import { getSliceState } from '../helpers';
import { selectJobIdsForUser } from '../users/slice';

import { SLICE_NAME } from './constants';
import {
  FetchRoleLaborTotalsParams,
  RoleLaborTotals,
  RoleLaborTotalsPayload,
  RoleLaborTotalsPayloadItem,
  RoleLaborTotalsState,
} from './types';

export const fetchRoleLaborTotals = createAsyncThunk<
  RoleLaborTotalsPayload,
  FetchRoleLaborTotalsParams,
  { state: RootState }
>(`${SLICE_NAME}/fetch`, async ({ jobIds, startDate, endDate }) => {
  const resp = await postJSON('/schedule_builder/labor', {
    data: {
      attributes: {
        job_ids: jobIds,
        start_date: startDate,
        end_date: endDate,
      },
      relationships: {},
    },
  });
  return resp;
});

const roleLaborTotalsAdapter = createEntityAdapter<RoleLaborTotals>();

export const initialState: RoleLaborTotalsState =
  roleLaborTotalsAdapter.getInitialState({
    status: 'idle',
    error: undefined,
  });

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    removeAll: roleLaborTotalsAdapter.removeAll,
  },
  extraReducers: builder => {
    builder.addCase(fetchRoleLaborTotals.pending, (state, action) => {
      state.status = 'pending';
      const jobIds = action.meta.arg.jobIds.split(',');
      const idsToRemove: string[] = [];

      Object.values(state.entities).forEach(entity => {
        if (entity && jobIds.includes(entity.jobId)) {
          idsToRemove.push(entity.id);
        }
      });
      roleLaborTotalsAdapter.removeMany(state, idsToRemove);
    });
    builder.addCase(fetchRoleLaborTotals.fulfilled, (state, action) => {
      const data = action.payload?.data ?? [];
      const items = data
        .filter(
          (item: RoleLaborTotalsPayloadItem) => item.type === 'roleLaborTotals'
        )
        .map((item: RoleLaborTotalsPayloadItem) => ({
          id: item.id,
          type: item.type,
          hours: item.attributes.hours,
          hasOvertime: item.attributes.hasOvertime,
          hourTotals: item.attributes.hourTotals,
          wages: item.attributes.wages,
          date: item.attributes.date,
          roleId: item.relationships.role.data?.id,
          jobId: item.relationships.job.data.id,
        }));
      roleLaborTotalsAdapter.upsertMany(state, items);
      state.status = 'succeeded';
    });
    builder.addCase(fetchRoleLaborTotals.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to fetch role labor totals';
    });
  },
});

export const { reducer, actions } = slice;

export const {
  selectById: selectRoleLaborTotalsById,
  selectIds: selectRoleLaborTotalsIds,
  selectEntities: selectRoleLaborTotalsEntities,
  selectAll: selectAllRoleLaborTotals,
  selectTotal: selectTotalRoleLaborTotals,
} = roleLaborTotalsAdapter.getSelectors<RootState>(
  state => getSliceState(state)[SLICE_NAME]
);

export const selectRoleLaborTotalsFetchIsPending = (
  state: RootState
): boolean => getSliceState(state)?.[SLICE_NAME]?.status === 'pending';

export const selectRoleLaborTotalsWhereAttribute = createCachedSelector(
  [
    selectAllRoleLaborTotals,
    (
      _: RootState,
      {
        attribute,
        value,
      }: {
        attribute: keyof RoleLaborTotals;
        value: RoleLaborTotals[keyof RoleLaborTotals];
      }
    ) => ({ attribute, value }),
  ],
  (roleLaborTotals, { attribute, value }) =>
    roleLaborTotals.filter(entity => entity && entity[attribute] === value)
)(
  (
    _,
    params?: {
      attribute: keyof RoleLaborTotals;
      value: RoleLaborTotals[keyof RoleLaborTotals];
    }
  ) =>
    `selectRoleLaborTotalsWhereAttribute-${params?.attribute}-${params?.value}`
);

export const selectDayLaborTotalForJobId = createCachedSelector(
  (state: RootState, props: { jobId: EntityId; date: string }) =>
    selectRoleLaborTotalsWhereAttribute(state, {
      attribute: 'date',
      value: props.date,
    }),
  (_, props) => props.jobId,
  (totals, jobId) =>
    totals.filter(roleLaborTotal => roleLaborTotal.jobId === jobId)
)((_, props) => `selectDayLaborTotalForJobId-${props.jobId}-${props.date}`);

export const selectRoleLaborTotalsByRoleId = createCachedSelector(
  (_: RootState, { roleId }: { roleId: EntityId }) => roleId,
  (state: RootState) => selectAllRoleLaborTotals(state),
  (state: RootState) => selectRoleLaborTotalsEntities(state),
  (roleId, roleLaborTotals, roleLaborTotalsMap) => {
    if (roleId === 'uncategorized') {
      return roleLaborTotals.filter(laborTotal => !laborTotal.roleId);
    }

    const ids: EntityId[] = [];

    for (const laborTotal of roleLaborTotals) {
      if (laborTotal.roleId === roleId) {
        ids.push(laborTotal.id);
      }
    }
    return ids.map(id => roleLaborTotalsMap[id]) || [];
  }
)((_, { roleId }) => `selectLaborTotalsByRoleId-${roleId}`);

export const selectLaborTotalsByRoleId = createCachedSelector(
  (_: RootState, { roleId }: { roleId: EntityId }) => roleId,
  (state: RootState, { roleId }: { roleId: EntityId }) => {
    if (roleId === 'uncategorized') {
      return [];
    }
    return selectRoleLaborTotalsByRoleId(state, { roleId }).map(
      (total: any) => total.id
    );
  },
  selectAllRoleLaborTotals,
  (roleId, roleLaborTotalIds, roleLaborTotals) => {
    if (roleId === 'uncategorized') {
      return roleLaborTotals.filter(laborTotal => !laborTotal.roleId);
    }
    return roleLaborTotals.filter(laborTotal =>
      roleLaborTotalIds.includes(laborTotal.id)
    );
  }
)((_, { roleId }) => `selectLaborTotalsByRoleId-${roleId}`);

export const selectRoleLaborTotalsByJobId = createCachedSelector(
  selectAllRoleLaborTotals,
  (_: RootState, props: { jobId: EntityId }) => props.jobId,
  selectCurrentDateRangeStart,
  selectCurrentDateRangeEnd,
  (roleLaborTotals, jobId, startDate, endDate) => {
    const range = datesInRange(startDate, endDate);
    return roleLaborTotals.filter(
      roleLaborTotal =>
        roleLaborTotal.jobId === jobId && range.includes(roleLaborTotal.date)
    );
  }
)((_, props) => `selectRoleLaborTotalsByJobId-${props.jobId}`);

export const selectRoleLaborTotalsByUserId = createCachedSelector(
  selectAllRoleLaborTotals,
  selectJobIdsForUser,
  selectCurrentDateRangeStart,
  selectCurrentDateRangeEnd,
  (roleLaborTotals, jobIds, startDate, endDate) => {
    const range = datesInRange(startDate, endDate);
    return roleLaborTotals.filter(
      roleLaborTotal =>
        jobIds.includes(roleLaborTotal.jobId) &&
        range.includes(roleLaborTotal.date)
    );
  }
)((_, props) => `selectRoleLaborTotalsByUserId-${props.jobId}`);

export const selectHourTotalsByJobId = createCachedSelector(
  (state: RootState, props: { date: string }) =>
    selectRoleLaborTotalsWhereAttribute(state, {
      attribute: 'date',
      value: props.date,
    }),
  totals => {
    const totalsByJobId: Record<string, any> = {};
    totals.forEach(total => {
      const jobId = total.jobId;
      totalsByJobId[jobId] = total.hourTotals;
    });
    return totalsByJobId;
  }
)((_, props) => `selectHourTotalsByJobId-${props.date}`);

export const selectOvernightRangeByJobId = createCachedSelector(
  selectOvernightShiftsForDay,
  selectShiftIdJobIdMap,
  (overnightShifts, shiftIdJobIdMap) => {
    const overnightRangeByJobId: Record<string, any> = {};
    overnightShifts.forEach((shift: any) => {
      const jobId = shiftIdJobIdMap[shift.id];

      overnightRangeByJobId[jobId] = getDecimalTimeRange({
        start: shift.startAt,
        end: shift.endAt,
        overnight: true,
      });
    });

    return overnightRangeByJobId;
  }
)(
  (_, props: { date: string }) =>
    `dayView-selectOvernightRangeByJobId-${props.date}`
);

export const selectOvernightHourTotalsByJob = createCachedSelector(
  selectOvernightRangeByJobId,
  selectHourTotalsByJobId,
  selectJobIdWageRateMap,
  (
    overnightRangeByJobId: Record<EntityId, any>,
    totalsByJobId: Record<EntityId, any>,
    jobIdWageRate: Record<EntityId, number>
  ) => {
    const updatedTotalsByJobId: Record<EntityId, any> = {};
    Object.entries(overnightRangeByJobId).forEach(
      ([jobId, overnightTimeRange]) => {
        let overnightTotals = {};
        if (overnightTimeRange) {
          overnightTotals = updateOvernightTotals({
            overnightTimeRange,
            overnightTotals,
            totals: totalsByJobId[jobId],
            wageRate: jobIdWageRate[jobId],
          });
        }
        updatedTotalsByJobId[jobId] = overnightTotals;
      }
    );
    return updatedTotalsByJobId;
  }
)(
  (_, props: { date: string }) =>
    `dayView-selectOvernightHourTotalsByJob-${props.date}`
);

export const selectRoleLaborTotalsForCurrentLocation = createCachedSelector(
  selectJobIdUserIdMap,
  (state: RootState, props: { date: string }) =>
    selectRoleLaborTotalsWhereAttribute(state, {
      attribute: 'date',
      value: props.date,
    }),
  (jobIdUserIdMap, laborTotals) =>
    laborTotals.filter(l => Object.keys(jobIdUserIdMap).includes(l?.jobId))
)(
  (_, props: { date: string }) =>
    `selectRoleLaborTotalsForCurrentLocation-${props.date}`
);

export const selectHourTotals = createCachedSelector(
  selectOvernightHourTotalsByJob,
  selectRoleLaborTotalsForCurrentLocation,
  (overnightHourTotals, totals) =>
    Array.from({ length: MAX_HOURS_IN_DAY_VIEW }, (_, hour) => hour).reduce(
      (memo, hour) => {
        memo[hour] ||= {
          hours: 0,
          wages: 0,
          employees: new Set(),
        };
        totals.forEach(total => {
          const totalJobId = total.jobId;

          let hourTotals = total.hourTotals;

          const overnightTotalExists =
            Object.keys(overnightHourTotals).includes(totalJobId);

          if (overnightTotalExists) {
            hourTotals = overnightHourTotals[totalJobId];
          }

          memo[hour].hours += hourTotals[hour]?.hours || 0;
          memo[hour].wages += hourTotals[hour]?.wages || 0;

          if (hourTotals[hour]?.employees) {
            memo[hour].employees = memo[hour].employees.union(
              new Set(hourTotals[hour].employees)
            );
          }
        });
        return memo;
      },
      [] as any[]
    )
)((_, props: { date: string }) => `selectHourTotals-${props.date}`);

export const selectHourHours = createCachedSelector(
  selectHourTotals,
  (_: RootState, props: { date: string; hour: number }) => props.hour,
  (totals, hour) => `${totals[hour].hours.toFixed(2)} hrs`
)((_, props) => `selectHourHours-${props.date}-${props.hour}`);

export const selectHoursWages = createCachedSelector(
  selectHourTotals,
  (_: RootState, props: { date: string; hour: number }) => props.hour,
  (totals, hour) => toCurrency(totals[hour].wages)
)((_, props) => `selectHoursWages-${props.date}-${props.hour}`);

export const selectHourEmployees = createCachedSelector(
  selectHourTotals,
  (_: RootState, props: { date: string; hour: number }) => props.hour,
  (totals, hour) => totals[hour].employees.size
)((_, props) => `selectHourEmployees-${props.date}-${props.hour}`);

export const selectSummaryHours = createCachedSelector(
  selectHourTotals,
  totals =>
    `${totals.reduce((memo, total) => memo + total.hours, 0).toFixed(2)} hrs`
)((_, props: { date: string }) => `selectSummaryHours-${props.date}`);

export const selectSummarysWages = createCachedSelector(
  selectHourTotals,
  totals => toCurrency(totals.reduce((memo, total) => memo + total.wages, 0))
)((_, props: { date: string }) => `selectSummarysWages-${props.date}`);

export const selectSummaryEmployees = createCachedSelector(
  selectHourTotals,
  totals =>
    totals.reduce((memo, total) => memo.union(total.employees), new Set()).size
)((_, props: { date: string }) => `selectSummaryEmployees-${props.date}`);

export const selectDateWages = createCachedSelector(
  selectRoleLaborTotalsForCurrentLocation,
  laborTotals =>
    laborTotals?.reduce((memo, totals) => memo + (totals.wages || 0), 0) || 0
)((_, props: { date: string }) => `selectDateWages-${props.date}`);

export const selectDateHours = createCachedSelector(
  selectRoleLaborTotalsForCurrentLocation,
  laborTotals =>
    laborTotals?.reduce((memo, totals) => memo + totals.hours, 0) || 0
)((_, props: { date: string }) => `selectDateHours-${props.date}`);

export const selectDayHoursForJob = createCachedSelector(
  selectDayLaborTotalForJobId,
  totals =>
    totals.reduce(
      (memo, roleLaborTotal) => memo + (roleLaborTotal.hours || 0),
      0
    )
)(
  (_, props: { jobId: EntityId; date: string }) =>
    `selectDayHoursForJob-${props.jobId}-${props.date}`
);

export const selectDayWagesForJob = createCachedSelector(
  selectDayLaborTotalForJobId,
  totals =>
    totals.reduce(
      (memo, roleLaborTotal) => memo + (roleLaborTotal.wages || 0),
      0
    )
)(
  (_, props: { jobId: EntityId; date: string }) =>
    `selectDayWagesForJob-${props.jobId}-${props.date}`
);

export const selectWagesForWeekByJobId = createCachedSelector(
  selectRoleLaborTotalsByJobId,
  roleLaborTotals =>
    roleLaborTotals?.reduce(
      (memo, roleLaborTotal) => memo + (roleLaborTotal.wages || 0),
      0
    )
)(
  (_, props: { jobId: EntityId }) => `selectWagesForWeekByJobId-${props.jobId}`
);

export const selectHoursForWeekByJobId = createCachedSelector(
  selectRoleLaborTotalsByJobId,
  roleLaborTotals =>
    roleLaborTotals?.reduce(
      (memo, roleLaborTotal) => memo + (roleLaborTotal.hours || 0),
      0
    )
)(
  (_, props: { jobId: EntityId }) => `selectHoursForWeekByJobId-${props.jobId}`
);

export const selectHasOvertimeForWeekByJobId = createCachedSelector(
  selectRoleLaborTotalsByJobId,
  roleLaborTotals =>
    roleLaborTotals?.some(roleLaborTotal => roleLaborTotal.hasOvertime)
)(
  (_, props: { jobId: EntityId }) =>
    `selectHasOvertimeForWeekByJobId-${props.jobId}`
);

export const selectHasOvertimeForWeekByUserId = createCachedSelector(
  selectRoleLaborTotalsByUserId,
  roleLaborTotals =>
    roleLaborTotals?.some(roleLaborTotal => roleLaborTotal.hasOvertime)
)(
  (_, props: { userId?: EntityId; jobId: EntityId }) =>
    `selectHasOvertimeForWeekByUserId-${props.userId}`
);

export const selectDayTotalHoursByRoleId = createCachedSelector(
  selectLaborTotalsByRoleId,
  laborTotals =>
    laborTotals.reduce((memo, labourTotal) => {
      memo += labourTotal.hours || 0;
      return memo;
    }, 0)
)(
  (_, { roleId }: { roleId: EntityId }) =>
    `selectDayTotalHoursByRoleId-${roleId}`
);

export const selectDayTotalWagesByRoleId = createCachedSelector(
  selectLaborTotalsByRoleId,
  laborTotals =>
    laborTotals.reduce((memo, labourTotal) => {
      memo += labourTotal.wages || 0;
      return memo;
    }, 0)
)(
  (_, { roleId }: { roleId: EntityId }) =>
    `selectDayTotalWagesByRoleId-${roleId}`
);
